import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { refreshAccessToken } from './tokenRefresh';

export const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// Mapping for equipment sub-resources to their actual API endpoints
const equipmentSubResourceMapping: Record<string, string> = {
  'equipment-maintenance': 'maintenance',
  'equipment-fuel': 'fuel',
  'equipment-inspections': 'inspections',
  'equipment-assignments': 'assignments',
  'equipment-incidents': 'incidents',
  'service-providers': 'service-providers',
};

// Code table resources that use the /code-tables endpoint
const codeTableResourceMapping: Record<string, string> = {
  'vehicle-utilizations': 'vehicle-utilizations',
  'commodities': 'vehicle-commodities',
};

// Helper function to build query parameters based on resource type
const buildQueryParams = (resource: string, params: any) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field, order } = params.sort || { field: 'id', order: 'DESC' };

  if (equipmentSubResourceMapping[resource]) {
    // Service providers and other equipment sub-resources use PageOptionsDto format
    return {
      page: page,
      perPage: perPage,
      orderByField: field,
      order: order.toLowerCase(), // asc/desc
      ...params.filter,
    };
  } else if (codeTableResourceMapping[resource]) {
    // Code table resources don't need pagination params (they return all data)
    return {};
  } else if (resource === 'completed-forms/reports') {
    // Reports endpoint expects PageOptionsDto params
    return {
      skip: (page - 1) * perPage,
      perPage: perPage,
      orderBy: JSON.stringify({ [field]: order.toLowerCase() }),
      where: params.filter ? JSON.stringify(params.filter) : undefined,
    };
  } else if (resource === 'driver-time-logs') {
    // Driver time logs uses skip/take pattern and expects filter as JSON string
    return {
      skip: (page - 1) * perPage,
      take: perPage,
      orderBy: JSON.stringify({ [field]: order.toLowerCase() }),
      filter: params.filter ? JSON.stringify(params.filter) : undefined,
    };
  } else {
    // Regular resources use the original format
    return {
      page: page,
      limit: perPage,
      sort: field,
      order: order.toLowerCase(),
      ...params.filter,
    };
  }
};

// Helper function to get the correct URL for equipment sub-resources
const getResourceUrl = (resource: string, params?: any) => {
  if (codeTableResourceMapping[resource]) {
    // Code table resources use the /code-tables endpoint
    const codeTableResource = codeTableResourceMapping[resource];
    return `${baseUrl}/code-tables/${codeTableResource}`;
  } else if (equipmentSubResourceMapping[resource]) {
    const subResource = equipmentSubResourceMapping[resource];
    
    // Service providers don't need equipmentId and always use the base endpoint
    if (resource === 'service-providers') {
      return `${baseUrl}/equipment/${subResource}`;
    }
    
    // Check various places where equipmentId might be provided for other equipment sub-resources
    if (params?.equipmentId) {
      return `${baseUrl}/equipment/${params.equipmentId}/${subResource}`;
    } else if (params?.target === 'equipmentId' && params?.id) {
      return `${baseUrl}/equipment/${params.id}/${subResource}`;
    } else if (params?.data?.equipmentId) {
      return `${baseUrl}/equipment/${params.data.equipmentId}/${subResource}`;
    } else if (params?.filter?.equipmentId) {
      // ReferenceManyField passes equipmentId in filter
      return `${baseUrl}/equipment/${params.filter.equipmentId}/${subResource}`;
    } else {
      // For reporting purposes (no equipmentId context), use top-level endpoints
      return `${baseUrl}/equipment/all-${subResource}`;
    }
  }
  return `${baseUrl}/${resource}`;
};

// Helper function for individual sub-resource operations (get one, update, delete)
const getSubResourceUrl = (resource: string, id: number) => {
  if (codeTableResourceMapping[resource]) {
    // Code table resources use the /code-tables endpoint
    const codeTableResource = codeTableResourceMapping[resource];
    return `${baseUrl}/code-tables/${codeTableResource}/${id}`;
  } else if (equipmentSubResourceMapping[resource]) {
    const subResource = equipmentSubResourceMapping[resource];
    
    // Service providers use /equipment/{subResource}/{id}
    if (resource === 'service-providers') {
      return `${baseUrl}/equipment/${subResource}/${id}`;
    }
    
    // For individual operations, use the pattern: /equipment/{subResource}/{id}
    return `${baseUrl}/equipment/${subResource}/${id}`;
  }
  return `${baseUrl}/${resource}/${id}`;
};

// HTTP client with JWT token support and tenant code header
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData (FormData sets its own with boundary)
  if (!(options.body instanceof FormData) && options.method !== 'GET' && options.method !== 'DELETE') {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Check if user is standalone (no tenants)
  const isStandalone = localStorage.getItem('isStandalone') === 'true';

  // Resources that don't require tenant context for standalone users
  const standaloneResources = ['drivers', 'driver-licenses', 'driver-certifications',
                                'driver-education', 'driver-employment', 'driver-vehicles',
                                'users', 'tenant-invitations'];
  const isStandaloneResource = standaloneResources.some(resource => url.includes(`/${resource}`));

  // Resources that are tenant-agnostic (should never have tenant header)
  const tenantAgnosticResources = ['code-tables'];
  const isTenantAgnosticResource = tenantAgnosticResources.some(resource => url.includes(`/${resource}`));

  // Don't add tenant header for standalone users accessing driver resources or for tenant-agnostic resources
  if (isStandalone && isStandaloneResource) {
    console.log('Standalone user accessing driver resource - skipping tenant header');
  } else if (isTenantAgnosticResource) {
    console.log('Accessing tenant-agnostic resource (code-tables) - skipping tenant header');
  } else {
    // Add tenant code header if multi-tenancy is enabled
    const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
    console.log('Tenants enabled:', tenantsEnabled);
    if (tenantsEnabled) {
      // Get current tenant from localStorage or context
      const currentTenantJson = localStorage.getItem('currentTenant');
      console.log('Current tenant from localStorage:', currentTenantJson);
      if (currentTenantJson) {
        try {
          const currentTenant = JSON.parse(currentTenantJson);
          console.log('Parsed current tenant:', currentTenant);
          if (currentTenant && currentTenant.code) {
            const tenantCode = currentTenant.code;
            options.headers.set('x-tenant-code', tenantCode);
            console.log('Setting x-tenant-code header:', tenantCode);
          }
        } catch (e) {
          console.error('Error parsing current tenant from localStorage:', e);
        }
      }
    } else {
      // When multi-tenancy is disabled, use default tenant
      options.headers.set('x-tenant-code', 'default');
      console.log('Setting x-tenant-code header to default');
    }
  }
  
  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (error: any) {
    // Handle auth errors
    if (error.status === 401) {
      console.log('Received 401, attempting to refresh token...');
      // Try to refresh the token
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        console.log('Token refreshed successfully, retrying request...');
        // Update the authorization header with the new token
        const newAccessToken = localStorage.getItem('accessToken');
        if (newAccessToken) {
          options.headers.set('Authorization', `Bearer ${newAccessToken}`);
        }

        // Retry the request with the new token
        try {
          const response = await fetchUtils.fetchJson(url, options);
          return response;
        } catch (retryError: any) {
          console.error('Retry failed after token refresh:', retryError);
          // If retry fails, logout
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('isStandalone');
          window.location.href = '/login';
          throw retryError;
        }
      } else {
        console.log('Token refresh failed, logging out...');
        // Refresh failed, logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isStandalone');
        window.location.href = '/login';
      }
    }
    throw error;
  }
};


export const customDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const query = buildQueryParams(resource, params);
    const url = `${getResourceUrl(resource, params.filter)}?${stringify(query)}`;
    try {
      // Lightweight request tracing to help diagnose potential filter loops
      console.debug('[DP:getList]', {
        resource,
        url,
        filter: params?.filter,
        pagination: params?.pagination,
        sort: params?.sort,
      });
      const { json, headers } = await httpClient(url);
      
      // Handle different response formats
      let data, total;
      
      if (json.data && json.metadata) {
        // Paginated API format: { data: [...], metadata: { total: ... } }
        data = json.data;
        total = json.metadata.total;
      } else if (json.point && json.metadata) {
        // Alternative API format: { point: [...], metadata: { total: ... } }
        data = json.point;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        // Direct array format
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        // Other formats
        data = json.data || json.point || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }
      
      return { data, total };
    } catch (error) {
      console.error(`Error fetching ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = getSubResourceUrl(resource, params.id);
    
    try {
      const { json } = await httpClient(url);
      return { data: json };
    } catch (error) {
      console.error(`Error fetching ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    // For code table resources and equipment sub-resources, we need to handle this differently
    // Since they don't have a bulk endpoint, we'll fetch individually
    if (codeTableResourceMapping[resource] || equipmentSubResourceMapping[resource]) {
      try {
        const promises = params.ids.map(id => 
          httpClient(getSubResourceUrl(resource, id))
            .then(({ json }) => json)
            .catch(() => null) // Handle individual failures gracefully
        );
        
        const results = await Promise.all(promises);
        const data = results.filter(item => item !== null);
        return { data };
      } catch (error) {
        console.error(`Error fetching multiple ${resource}:`, error);
        return { data: [] };
      }
    } else {
      // Regular resources
      const query = {
        filter: JSON.stringify({ id: params.ids }),
      };
      const url = `${getResourceUrl(resource)}?${stringify(query)}`;
      
      try {
        const { json } = await httpClient(url);
        // Handle both array responses and paginated responses with data property
        const data = Array.isArray(json) ? json : (json.data || []);
        return { data };
      } catch (error) {
        console.error(`Error fetching multiple ${resource}:`, error);
        return { data: [] };
      }
    }
  },

  getManyReference: async (resource, params) => {
    // For driver-time-logs, we need to add the userId to the filter
    if (resource === 'driver-time-logs' && params.target === 'userId') {
      params.filter = { ...params.filter, userId: params.id };
    }
    
    // For completed-forms/reports, we need to add the userId to the filter
    if (resource === 'completed-forms/reports' && params.target === 'userId') {
      params.filter = { ...params.filter, userId: params.id };
    }
    
    const query = buildQueryParams(resource, params);
    
    // For equipment sub-resources, we need to use the equipment ID from params
    let url;
    if (codeTableResourceMapping[resource]) {
      // Code table resources don't use getManyReference pattern - just use getList
      url = `${getResourceUrl(resource, params)}?${stringify(query)}`;
    } else if (equipmentSubResourceMapping[resource]) {
      // For equipment sub-resources, params.id is the equipment ID
      url = `${baseUrl}/equipment/${params.id}/${equipmentSubResourceMapping[resource]}?${stringify(query)}`;
    } else if (resource === 'driver-time-logs' || resource === 'completed-forms/reports') {
      // These resources handle filtering via the filter parameter in buildQueryParams
      url = `${getResourceUrl(resource, params)}?${stringify(query)}`;
    } else {
      // For non-equipment sub-resources, add the target parameter
      query[params.target] = params.id;
      url = `${getResourceUrl(resource, params)}?${stringify(query)}`;
    }
    
    try {
      const { json, headers } = await httpClient(url);
      
      // Handle different response formats
      let data, total;
      
      if (json.data && json.metadata) {
        // Paginated API format: { data: [...], metadata: { total: ... } }
        data = json.data;
        total = json.metadata.total;
      } else if (json.point && json.metadata) {
        // Alternative API format: { point: [...], metadata: { total: ... } }
        data = json.point;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        // Direct array format
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        // Other formats
        data = json.data || json.point || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }
      
      return { data, total };
    } catch (error) {
      console.error(`Error fetching ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    const url = getResourceUrl(resource, params);

    // Check if params.data is already a FormData object (e.g., from frontend)
    const isFormDataAlready = params.data instanceof FormData;

    try {
      let requestOptions: any = { method: 'POST' };

      // If it's already FormData, use it as-is (preserves all fields)
      if (isFormDataAlready) {
        requestOptions.body = params.data;
      } else {
        // Check if we have file uploads
        const hasFiles = params.data && (
          (params.data.ownershipPermitFiles && params.data.ownershipPermitFiles.length > 0) ||
          (params.data.weightRatingFiles && params.data.weightRatingFiles.length > 0) ||
          (resource === 'files' && params.data.file) ||
          (resource === 'drivers/licenses' && params.data.file)
        );

        if (hasFiles) {
          // Use FormData for file uploads
          const formData = new FormData();

          // Add all regular fields to FormData
          Object.keys(params.data).forEach(key => {
            if (key !== 'ownershipPermitFiles' && key !== 'weightRatingFiles' && key !== 'file') {
              // Handle boolean values properly
              if (typeof params.data[key] === 'boolean') {
                formData.append(key, params.data[key].toString());
              } else if (params.data[key] !== undefined && params.data[key] !== null) {
                formData.append(key, params.data[key]);
              }
            }
          });

          // Add single file for files resource and driver licenses
          if ((resource === 'files' || resource === 'drivers/licenses') && params.data.file) {
            formData.append('file', params.data.file);
            // The backend expects 'folder' instead of 'folderPath'
            if (params.data.folderPath) {
              formData.append('folder', params.data.folderPath);
            }
          }

          // Add ownership permit files
          if (params.data.ownershipPermitFiles) {
            params.data.ownershipPermitFiles.forEach((file: File) => {
              formData.append('ownershipPermitFiles', file);
            });
          }

          // Add weight rating files
          if (params.data.weightRatingFiles) {
            params.data.weightRatingFiles.forEach((file: File) => {
              formData.append('weightRatingFiles', file);
            });
          }

          requestOptions.body = formData;
          // Don't set Content-Type header, let browser set it with boundary
        } else {
          // Use JSON for regular data
          requestOptions.body = JSON.stringify(params.data);
        }
      }

      const { json } = await httpClient(url, requestOptions);
      
      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    let url: string;
    if (equipmentSubResourceMapping[resource]) {
      url = getSubResourceUrl(resource, params.id);
    } else {
      url = `${getResourceUrl(resource)}/${params.id}`;
    }

    // Check if params.data is already a FormData object (e.g., from frontend)
    const isFormDataAlready = params.data instanceof FormData;

    try {
      let requestOptions: any = { method: 'PATCH' };

      // If it's already FormData, use it as-is (preserves all fields like fileId)
      if (isFormDataAlready) {
        requestOptions.body = params.data;
      } else {
        // Check if we have file uploads
        const hasFiles = params.data && (
          (params.data.ownershipPermitFiles && params.data.ownershipPermitFiles.length > 0) ||
          (params.data.weightRatingFiles && params.data.weightRatingFiles.length > 0) ||
          (resource === 'drivers/licenses' && params.data.file)
        );

        if (hasFiles) {
          // Use FormData for file uploads
          const formData = new FormData();

          // Add all regular fields to FormData
          Object.keys(params.data).forEach(key => {
            if (key !== 'ownershipPermitFiles' && key !== 'weightRatingFiles' && key !== 'file') {
              // Handle boolean values properly
              if (typeof params.data[key] === 'boolean') {
                formData.append(key, params.data[key].toString());
              } else if (params.data[key] !== undefined && params.data[key] !== null) {
                formData.append(key, params.data[key]);
              }
            }
          });

          // Add single file for driver licenses
          if (resource === 'drivers/licenses' && params.data.file) {
            formData.append('file', params.data.file);
          }

          // Add ownership permit files
          if (params.data.ownershipPermitFiles) {
            params.data.ownershipPermitFiles.forEach((file: File) => {
              formData.append('ownershipPermitFiles', file);
            });
          }

          // Add weight rating files
          if (params.data.weightRatingFiles) {
            params.data.weightRatingFiles.forEach((file: File) => {
              formData.append('weightRatingFiles', file);
            });
          }

          requestOptions.body = formData;
          // Don't set Content-Type header, let browser set it with boundary
        } else {
          // Use JSON for regular data
          requestOptions.body = JSON.stringify(params.data);
        }
      }

      const { json } = await httpClient(url, requestOptions);

      return { data: json };
    } catch (error) {
      console.error(`Error updating ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      const responses = await Promise.all(
        params.ids.map(id => 
          httpClient(getSubResourceUrl(resource, id), {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );
      
      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = getSubResourceUrl(resource, params.id);
    
    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });
      
      return { data: json };
    } catch (error) {
      console.error(`Error deleting ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const responses = await Promise.all(
        params.ids.map(id => 
          httpClient(getSubResourceUrl(resource, id), {
            method: 'DELETE',
          })
        )
      );
      
      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple ${resource}:`, error);
      throw error;
    }
  },
};